import { FC, useMemo, useRef } from 'react';
import { Input } from 'src/components/Input';
import { Textarea } from 'src/components/Textarea';
import { Option } from 'src/components/SelectBox';
import { CommunicationTiming } from 'src/types/automation';
import StepCard from '../step-card/StepCard';
import type { StepMetrics } from '../step-card/StepCardHeader';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import { CalendarBlank, EnvelopeSimple } from '@phosphor-icons/react';
import StepTimingControls from '../step-card/StepTimingControls';
import AddVariableButton from 'src/components/AddVariableButton';
import { extractVariables } from 'src/utils/extractVariables';

type EmailStepCardProps = {
  stepNumber: number;
  subject: string;
  body: string;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  communicationTiming?: CommunicationTiming;
  onCommunicationTimingChange?: (timing: CommunicationTiming) => void;
  timeWindowOptions?: Option[];
  onTimeWindowChange?: (option: Option) => void;
  selectedTimeWindowOption?: Option;
  onTitleChange?: (value: string) => void;
  metrics?: StepMetrics;
  addStepAbove?: () => void;
  addStepBelow?: () => void;
  deleteStep?: () => void;
  hideTimeWindow?: boolean;
  disabled?: boolean;
};

export const EmailStepCard: FC<EmailStepCardProps> = ({
  stepNumber,
  subject,
  body,
  onSubjectChange,
  onBodyChange,
  onTitleChange,
  communicationTiming,
  onCommunicationTimingChange,
  timeWindowOptions,
  selectedTimeWindowOption,
  onTimeWindowChange,
  metrics,
  addStepAbove,
  addStepBelow,
  deleteStep,
  hideTimeWindow = false,
  disabled = false,
}) => {
  const isError = !subject.trim() || !body.trim();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const variables = useMemo(
    () => extractVariables(`${subject}\n${body}`),
    [subject, body],
  );

  const getError = () => {
    if (!subject.trim() && !body.trim()) {
      return 'Email still needs subject-line and body';
    }
    if (!subject.trim()) {
      return 'Email still needs subject-line';
    }
    if (!body.trim()) {
      return 'Email still needs body';
    }
    return '';
  };

  const summary = (
    <div className="flex flex-wrap items-center gap-6">
      {isError && getError() && (
        <div className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-error/10">
          <EnvelopeSimple className="h-5 w-5" />
          <span>{getError()}</span>
        </div>
      )}
      {subject.trim() && (
        <div className="inline-flex items-center gap-2 min-w-0">
          <EnvelopeSimple className="h-5 w-5 text-neutral-500" />
          <span className="text-neutral-700 truncate max-w-[25rem]">
            {subject}
          </span>
        </div>
      )}
      <div className="inline-flex items-center gap-2">
        <CalendarBlank className="h-5 w-5 text-neutral-500" />
        <span className="text-neutral-700">
          {communicationTiming === 'earliest'
            ? 'At the earliest eligible time'
            : selectedTimeWindowOption?.label}
        </span>
      </div>
    </div>
  );
  return (
    <StepCard
      stepNumber={stepNumber}
      titleDefaultValue={{
        value: CommunicationChannel.EMAIL,
        label: 'Email',
      }}
      titleOnChange={onTitleChange}
      metrics={metrics}
      summary={summary}
      error={isError}
      onAddStepAbove={addStepAbove}
      onAddStepBelow={addStepBelow}
      onDeleteStep={deleteStep}
      disabled={disabled}
    >
      <div className="w-full sm:w-full flex-shrink-0 flex flex-col gap-y-6">
        <div className="w-full rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <div className="space-y-0">
            <Input
              type="text"
              placeholder="Subject"
              value={subject}
              onChange={(e) => onSubjectChange(e.target.value)}
              variant="text"
              color="default"
              className="w-full bg-transparent hover:bg-transparent focus:bg-transparent shadow-none"
              disabled={disabled}
            />
            <div className="h-px bg-neutral-200 mx-1" />
            <Textarea
              placeholder="Write your email..."
              rows={8}
              value={body}
              onChange={(e) => onBodyChange(e.target.value)}
              variant="text"
              color="default"
              className="w-full bg-transparent hover:bg-transparent focus:bg-transparent shadow-none"
              ref={textareaRef}
              disabled={disabled}
            />
            <AddVariableButton
              textareaRef={textareaRef}
              variables={variables}
              position="top-left"
              disabled={disabled}
            />
          </div>
        </div>
        {/* TODO: Add back in when we have a way to set the time window for calls
        {!hideTimeWindow && (
          <StepTimingControls
            label="When to send email"
            timing={communicationTiming}
            onTimingChange={onCommunicationTimingChange}
            timeWindowOptions={timeWindowOptions}
            selectedTimeWindowOption={selectedTimeWindowOption}
            onTimeWindowChange={onTimeWindowChange}
            disabled={disabled}
          />
        )} */}
      </div>
    </StepCard>
  );
};

export default EmailStepCard;
